import type React from 'react'

import { AnimatePresence, motion } from 'framer-motion'

import { cn } from '@/utils/shadcn/utils'

/**
 * Duration for chart loading transitions in seconds
 */
const CHART_TRANSITION_DURATION = 0.2

interface ChartLoadingStateContainerProps {
  children: React.ReactNode
  isLoading?: boolean
  height?: string
}

interface ChartContentContainerProps {
  children: React.ReactNode
  isLoading?: boolean
  height?: string
}

/**
 * Container component for chart loading state content.
 * Shows content with fade-in animation when loading is true,
 * fades out when loading becomes false.
 */
export function ChartLoadingStateContainer({
  children,
  isLoading,
  height,
}: ChartLoadingStateContainerProps) {
  return (
    <AnimatePresence>
      {isLoading && (
        <motion.div
          animate={{
            opacity: 1,
            display: 'block',
          }}
          exit={{
            opacity: 0,
            display: 'none',
          }}
          transition={{
            duration: CHART_TRANSITION_DURATION,
            ease: 'easeOut',
          }}
          className={cn('absolute top-0 left-0 w-full', height)}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  )
}

/**
 * Container component for chart main content.
 * Shows content with fade-in animation when loading is false,
 * hidden when loading is true.
 */
export function ChartContentContainer({
  children,
  isLoading,
  height,
}: ChartContentContainerProps) {
  return (
    <AnimatePresence>
      {!isLoading && (
        <motion.div
          initial={{
            opacity: 0,
            display: 'none',
          }}
          animate={{
            opacity: 1,
            display: 'block',
          }}
          transition={{
            duration: CHART_TRANSITION_DURATION,
            ease: 'easeOut',
          }}
          className={cn('opacity-1 absolute top-0 left-0 w-full', height)}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  )
}

/**
 * Base container component that provides relative positioning
 * for the chart loading and content containers.
 */
export function ChartTransitionContainer({
  children,
  height = 'h-auto',
  className,
}: {
  children: React.ReactNode
  height?: string
  className?: string
}) {
  return <div className={cn('relative', height, className)}>{children}</div>
}
